import React from 'react';
import {
  RichTextEditor,
  RichTextEditorControl,
} from '@mantine/tiptap';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import {
  TEXT_ALIGN_GLYPHS, TEXT_STYLE_GLYPHS, duotone,
} from '../../../puck/fields/TextStyleToggles';

/* Glyphes de la barre de texte du classique, partagés avec les réglages du
   thème et des liens (cf. TextStyleToggles). */

function BubbleCommandControl({
  rememberNow,
  runWithSelection,
  active,
  label,
  glyph,
  run,
}) {
  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    rememberNow();
    runWithSelection(run);
  };

  return (
    <RichTextEditorControl
      active={active}
      aria-label={label}
      title={label}
      onMouseDown={handleMouseDown}
    >
      {glyph}
    </RichTextEditorControl>
  );
}

export function BubbleFormatControls({ editor, rememberNow, runWithSelection }) {
  return (
    <RichTextEditor.ControlsGroup>
      <BubbleCommandControl
        rememberNow={rememberNow}
        runWithSelection={runWithSelection}
        active={editor.isActive('bold')}
        label="Gras"
        glyph={TEXT_STYLE_GLYPHS.bold}
        run={(c) => c.toggleBold()}
      />
      <BubbleCommandControl
        rememberNow={rememberNow}
        runWithSelection={runWithSelection}
        active={editor.isActive('italic')}
        label="Italique"
        glyph={TEXT_STYLE_GLYPHS.italic}
        run={(c) => c.toggleItalic()}
      />
      <BubbleCommandControl
        rememberNow={rememberNow}
        runWithSelection={runWithSelection}
        active={editor.isActive('underline')}
        label="Souligné"
        glyph={TEXT_STYLE_GLYPHS.underline}
        run={(c) => c.toggleUnderline()}
      />
      <BubbleCommandControl
        rememberNow={rememberNow}
        runWithSelection={runWithSelection}
        active={editor.isActive('strike')}
        label="Barré"
        glyph={duotone(icon({ name: 'strikethrough', family: 'duotone', style: 'solid' }))}
        run={(c) => c.toggleStrike()}
      />
      <BubbleCommandControl
        rememberNow={rememberNow}
        runWithSelection={runWithSelection}
        label="Effacer le format"
        glyph={duotone(icon({ name: 'text-slash', family: 'duotone', style: 'solid' }))}
        run={(c) => c.unsetAllMarks().clearNodes()}
      />
    </RichTextEditor.ControlsGroup>
  );
}

export function BubbleAlignControls({ editor, rememberNow, runWithSelection }) {
  return (
    <RichTextEditor.ControlsGroup>
      <BubbleCommandControl
        rememberNow={rememberNow}
        runWithSelection={runWithSelection}
        active={editor.isActive({ textAlign: 'left' })}
        label="Aligner à gauche"
        glyph={TEXT_ALIGN_GLYPHS.left}
        run={(c) => c.setTextAlign('left')}
      />
      <BubbleCommandControl
        rememberNow={rememberNow}
        runWithSelection={runWithSelection}
        active={editor.isActive({ textAlign: 'center' })}
        label="Centrer"
        glyph={TEXT_ALIGN_GLYPHS.center}
        run={(c) => c.setTextAlign('center')}
      />
      <BubbleCommandControl
        rememberNow={rememberNow}
        runWithSelection={runWithSelection}
        active={editor.isActive({ textAlign: 'justify' })}
        label="Justifier"
        glyph={TEXT_ALIGN_GLYPHS.justify}
        run={(c) => c.setTextAlign('justify')}
      />
      <BubbleCommandControl
        rememberNow={rememberNow}
        runWithSelection={runWithSelection}
        active={editor.isActive({ textAlign: 'right' })}
        label="Aligner à droite"
        glyph={TEXT_ALIGN_GLYPHS.right}
        run={(c) => c.setTextAlign('right')}
      />
    </RichTextEditor.ControlsGroup>
  );
}

export function BubbleListControls({ editor, rememberNow, runWithSelection }) {
  return (
    <RichTextEditor.ControlsGroup>
      <BubbleCommandControl
        rememberNow={rememberNow}
        runWithSelection={runWithSelection}
        active={editor.isActive('bulletList')}
        label="Liste à puces"
        glyph={duotone(icon({ name: 'list-ul', family: 'duotone', style: 'solid' }))}
        run={(c) => c.toggleBulletList()}
      />
      <BubbleCommandControl
        rememberNow={rememberNow}
        runWithSelection={runWithSelection}
        active={editor.isActive('orderedList')}
        label="Liste numérotée"
        glyph={duotone(icon({ name: 'list-ol', family: 'duotone', style: 'solid' }))}
        run={(c) => c.toggleOrderedList()}
      />
    </RichTextEditor.ControlsGroup>
  );
}

/* Le menu `@` s'ouvre quand le `@` suit un début de bloc ou une espace : hors
   de ce cas, le bouton pose l'espace lui-même (le classique insère ' @'). */
const needsSpaceBeforeAt = (editor) => {
  const { $from } = editor.state.selection;
  const before = $from.parent.textBetween(0, $from.parentOffset, undefined, '\uFFFC');
  return before !== '' && !/[\s\u00A0]$/.test(before);
};

export function BubbleVariableControl({ editor, rememberNow, runWithSelection }) {
  return (
    <RichTextEditor.ControlsGroup>
      <BubbleCommandControl
        rememberNow={rememberNow}
        runWithSelection={runWithSelection}
        label="Variable texte"
        glyph={duotone(icon({ name: 'at', family: 'duotone', style: 'solid' }))}
        run={(c) => c.insertContent(needsSpaceBeforeAt(editor) ? ' @' : '@')}
      />
    </RichTextEditor.ControlsGroup>
  );
}
